import React, { useEffect, useRef, useState } from 'react';

export function useCollapseOnScroll(
  scrollRef?: React.RefObject<HTMLElement | null>,
  collapseAt = 72,
  expandAt = 32,
) {
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    const target: HTMLElement | Window = scrollRef?.current ?? window;

    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const scrollTop = target instanceof Window ? window.scrollY : target.scrollTop;
        setIsCollapsed((prev) => {
          if (!prev && scrollTop > collapseAt) return true;
          if (prev && scrollTop < expandAt) return false;
          return prev;
        });
        ticking = false;
      });
    };

    target.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => target.removeEventListener('scroll', handleScroll);
  }, [scrollRef, collapseAt, expandAt]);

  return isCollapsed;
}

interface AdaptiveStickyNavProps {
  isCollapsed: boolean;
  children: React.ReactNode;
  zIndex?: number;
  className?: string;
}

export function AdaptiveStickyNav({
  isCollapsed,
  children,
  zIndex = 30,
  className = '',
}: AdaptiveStickyNavProps) {
  return (
    <div
      className={`sticky top-0 transition-shadow duration-300 ease-out ${className}`}
      style={{
        zIndex,
        boxShadow: isCollapsed ? 'var(--elevation-medium)' : 'none',
      }}
    >
      {children}
    </div>
  );
}

export function useScrollContainerRef<T extends HTMLElement>() {
  return useRef<T>(null);
}
